import { Icon } from 'components/Icon'
import { IconName } from 'components/Icon/iconNames'
import { Text } from 'components/Text'
import { ComponentProps, useState } from 'react'
import { Pill } from '../../../components/Pill'
import { CollapsibleMenuRoot, Content, MenuTrigger, Wrapper } from './CollapsibleMenu.styles'
import { useTranslation } from 'hooks/useTranslation'
import { InventoryStatus } from '../../../models/Inventory'
import { Stack } from 'components/Stack'
import { Tooltip } from 'components/Tooltip'

type MenuOwnProps = ComponentProps<typeof CollapsibleMenuRoot>

export type CollapsibleMenuProps = MenuOwnProps & {
  label: string
  interactiveElement?: JSX.Element
  hasMaxWidth?: boolean
  labelColor?: any
  variant?: 'productPage' | 'step4' | 'flexBox'
  isUnavailable?: boolean
  outOfStockEndDate?: string
}

export const CollapsibleMenu = ({
  label,
  interactiveElement,
  children,
  hasMaxWidth = true,
  labelColor = 'primary-700',
  variant,
  isUnavailable = false,
  outOfStockEndDate,
  ...rest
}: CollapsibleMenuProps) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const iconName: IconName = isOpen ? 'arrow-up-line' : 'arrow-down-line'
  const step4IconName: IconName = isOpen ? 'minus' : 'plus'

  return (
    <CollapsibleMenuRoot
      variant={variant}
      onOpenChange={() => setIsOpen(!isOpen)}
      defaultOpen={false}
      {...rest}
    >
      <Wrapper css={{ paddingLeft: interactiveElement && variant !== 'step4' ? '$3' : 0 }}>
        {interactiveElement}
        <MenuTrigger
          variant={variant}
          aria-label={label}
          css={{ justifyContent: interactiveElement ? 'flex-end' : 'space-between' }}
        >
          {!interactiveElement && (
            <Text
              size={variant === 'step4' ? 'body' : 'headline'}
              weight={variant === 'step4' ? 'regular' : 'medium'}
              color={labelColor}
              align="left"
            >
              <Stack
                direction={{ '@initial': 'column', '@bp3': 'row' }}
                gap="1"
                alignItems="center"
                justifyContent={{ '@initial': 'flex-start', '@bp3': 'center' }}
              >
                <span>{label}</span>
                {isUnavailable && (
                  <Stack direction="row" gap="1" alignItems="center">
                    <Pill variant="unavailable">
                      {t(`status:products.${InventoryStatus.OUT_OF_STOCK}`)}
                    </Pill>
                    {outOfStockEndDate && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <Tooltip
                          text={t('status:estimated-to-be-in-stock', { date: outOfStockEndDate })}
                          instant
                          side="top"
                        >
                          <Stack alignItems="center" justifyContent="center">
                            <Icon name="information-circle-line" size="3" color="accent-gray" />
                          </Stack>
                        </Tooltip>
                      </div>
                    )}
                  </Stack>
                )}
              </Stack>
            </Text>
          )}
          <Icon
            name={variant === 'step4' ? step4IconName : iconName}
            color={variant === 'step4' ? 'primary-500' : 'accent-black'}
          />
        </MenuTrigger>
      </Wrapper>
      <Content hasMaxWidth={hasMaxWidth} variant={variant}>
        {children}
      </Content>
    </CollapsibleMenuRoot>
  )
}

CollapsibleMenu.displayName = 'CollapsibleMenu'
